import { PlanVisitCreateDraft, PlanVisitDayDraft } from '../../../types/visitas';
import { eachIsoDayInRange, visitRowKey } from './planFormHelpers';
import { VisitRowState } from './planVisitaFormUi';

export type DraftByDay = Record<string, VisitRowState[]>;

const DOW_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export const formatDayTabLabel = (isoDay: string, visitCount?: number): string => {
  const date = new Date(`${isoDay}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoDay;
  const dow = DOW_SHORT[date.getDay()];
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const suffix = visitCount !== undefined && visitCount > 0 ? ` (${visitCount})` : '';
  return `${dow} ${dd}/${mm}${suffix}`;
};

export const emptyDraftByDay = (rangeDays: string[]): DraftByDay => {
  const draft: DraftByDay = {};
  rangeDays.forEach((day) => {
    draft[day] = [];
  });
  return draft;
};

export const syncDraftByDayToRange = (prev: DraftByDay, rangeDays: string[]): DraftByDay => {
  const next: DraftByDay = {};
  rangeDays.forEach((day) => {
    next[day] = prev[day] ?? [];
  });
  return next;
};

export const stripAgendaRowsFromDraftByDay = (prev: DraftByDay): DraftByDay => {
  const next: DraftByDay = {};
  Object.entries(prev).forEach(([day, rows]) => {
    next[day] = rows.filter((row) => !row.fromAgenda);
  });
  return next;
};

export const mergeAgendaIntoDraftByDay = (
  prev: DraftByDay,
  agendaByDay: DraftByDay,
): DraftByDay => {
  const next = { ...prev };
  Object.entries(agendaByDay).forEach(([day, agendaRows]) => {
    const manualRows = (prev[day] ?? []).filter((row) => !row.fromAgenda);
    const keys = new Set(manualRows.map((row) => visitRowKey(row.nroCliente, row.codigoDireccion)));
    const merged = [...manualRows];
    agendaRows.forEach((row) => {
      const key = visitRowKey(row.nroCliente, row.codigoDireccion);
      if (!keys.has(key)) {
        merged.push(row);
        keys.add(key);
      }
    });
    next[day] = merged;
  });
  return next;
};

export const rowToVisitDraft = (row: VisitRowState): PlanVisitCreateDraft | null => {
  if (Number(row.nroCliente) <= 0) return null;
  return {
    nroCliente: row.nroCliente,
    codigoDireccion: row.codigoDireccion,
    observaciones: row.observaciones || null,
    horarioPreferido: row.horarioPreferido,
  };
};

export const buildVisitasPorDiaPayload = (
  draftByDay: DraftByDay,
  rangeDays: string[],
): PlanVisitDayDraft[] =>
  rangeDays
    .map((fecha) => {
      const visitDrafts = (draftByDay[fecha] ?? [])
        .map(rowToVisitDraft)
        .filter((draft): draft is PlanVisitCreateDraft => draft !== null);
      return { fecha, visitDrafts };
    })
    .filter((day) => day.visitDrafts.length > 0);

export const countValidVisitsInDraft = (draftByDay: DraftByDay): number =>
  Object.values(draftByDay).reduce(
    (total, rows) => total + rows.filter((row) => Number(row.nroCliente) > 0).length,
    0,
  );

export const countDaysWithVisits = (draftByDay: DraftByDay, rangeDays: string[]): number =>
  rangeDays.filter((day) =>
    (draftByDay[day] ?? []).some((row) => Number(row.nroCliente) > 0),
  ).length;

export const countRowsInDraftByDay = (draftByDay: DraftByDay): number =>
  Object.values(draftByDay).reduce((total, rows) => total + rows.length, 0);

export const makeRowRefKey = (day: string, index: number): string => `${day}|${index}`;

export { eachIsoDayInRange };
